import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, IconButton, Tooltip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { adminClient } from './data/adminClient';
import { useEventReadOnly } from './adminAccess';

const EMPTY_FORM = { item: '', price: '' };

// Item names (column A of the Prices tab) are matched by name elsewhere —
// entryRateFor() in feeCalculations.js, and the "School Registration Fee"/
// "Participation Fee" lookups used across the registration forms and
// invoices — deleting or renaming one of those rows makes the matching
// form/invoice silently fall back to $0 rather than erroring, so the delete
// confirmation calls that out explicitly.
export default function EventPricesSection() {
  const readOnly = useEventReadOnly(); // Event Viewer: prices shown, no add/edit/delete
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminClient.getPrices());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const startEdit = (row) => {
    setEditingRow(row.rowIndex);
    setEditValue(String(row.price));
    setError(null);
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditValue('');
  };

  const saveEdit = async (rowIndex) => {
    const price = Number(editValue);
    if (isNaN(price) || price < 0) {
      setError('Enter a valid, non-negative price.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await adminClient.updatePrice(rowIndex, price);
      setRows(prev => prev.map(r => (r.rowIndex === rowIndex ? { ...r, price } : r)));
      setEditingRow(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setAddForm(EMPTY_FORM);
    setAddError(null);
    setAddDialogOpen(true);
  };

  const handleAdd = async () => {
    const price = Number(addForm.price);
    if (!addForm.item.trim()) { setAddError('Item name is required.'); return; }
    if (isNaN(price) || price < 0) { setAddError('Enter a valid, non-negative price.'); return; }
    setAdding(true);
    setAddError(null);
    try {
      await adminClient.addPrice(addForm.item.trim(), price);
      setAddDialogOpen(false);
      await fetchRows();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await adminClient.deletePrice(deleteConfirm.rowIndex);
      setDeleteConfirm(null);
      await fetchRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>;

  return (
    <>
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openAdd}>
            Add Price
          </Button>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No prices configured.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                {!readOnly && <TableCell sx={{ fontWeight: 600, width: 110 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const isEditing = editingRow === row.rowIndex;
                return (
                  <TableRow key={row.rowIndex} hover>
                    <TableCell><Typography variant="body2" fontWeight={500}>{row.item}</Typography></TableCell>
                    <TableCell sx={{ width: 140 }}>
                      {isEditing ? (
                        <TextField
                          size="small" type="number" autoFocus
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveEdit(row.rowIndex);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography> }}
                          sx={{ width: 120 }}
                        />
                      ) : (
                        <Typography variant="body2">${row.price.toFixed(2)}</Typography>
                      )}
                    </TableCell>
                    {!readOnly && <TableCell>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title="Save">
                            <span>
                              <IconButton size="small" color="primary" onClick={() => saveEdit(row.rowIndex)} disabled={saving}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <span>
                              <IconButton size="small" onClick={cancelEdit} disabled={saving}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title="Edit price">
                            <IconButton size="small" onClick={() => startEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteConfirm(row)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add dialog */}
      <Dialog open={addDialogOpen} onClose={() => !adding && setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Price</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {addError && <Alert severity="error">{addError}</Alert>}
            <TextField
              label="Item name *" size="small" fullWidth
              value={addForm.item}
              onChange={e => setAddForm(prev => ({ ...prev, item: e.target.value }))}
            />
            <TextField
              label="Price *" size="small" fullWidth type="number"
              value={addForm.price}
              onChange={e => setAddForm(prev => ({ ...prev, price: e.target.value }))}
              InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography> }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={adding}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={adding}>
            {adding ? <CircularProgress size={18} color="inherit" /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => !deleting && setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Price</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Remove <strong>{deleteConfirm?.item}</strong> from the price list? This cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            If this item's name is referenced by fee calculations, invoices, or the registration
            forms (e.g. "School Registration Fee", "Participation Fee", or a dance entry size),
            removing it will make that amount silently show as $0 instead of erroring.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
